import { useEffect, useRef, useState } from "react";

/**
 * Hook for encapsulating logic for rendering a Google reCAPTCHA widget. Functions are provided for rendering
 * and resetting  the reCAPTCHA. The hook also handles the token state.
 * @param {string} props.siteKey The site key for the reCAPTCHA widget. This is a public key, so it's okay to expose on the frontend.
 */
export function useReCAPTCHA(siteKey) {
    /** @type {[number | null, React.Dispatch<React.SetStateAction<number | null>>]} */
    const [widgetId, setWidgetId] = useState(null);
    /** @type {[string | null, React.Dispatch<React.SetStateAction<string | null>>]} */
    const [reCAPTCHAToken, setReCAPTCHAToken] = useState(null);

    /**
     * @param {React.MutableRefObject<HTMLElement | null>} ref ReCAPTCHA will be rendered into the element this ref is attached to.
     */
    function renderReCAPTCHA(ref) {
        if (!window.grecaptcha) {
            throw new Error(
                "The Google reCAPTCHA script must be loaded before calling the renderReCAPTCHA function. To load " +
                    'the script, add <script src="https://www.google.com/recaptcha/api.js" async defer></script> to the ' +
                    "<head></head> element.",
            );
        }

        window.grecaptcha.ready(() => {
            const widgetId = window.grecaptcha.render(ref.current, {
                sitekey: siteKey,
                callback: (token) => {
                    setReCAPTCHAToken(token);
                },
                "expired-callback": () => {
                    resetReCAPTCHA();
                },
            });

            setWidgetId(widgetId);
        });
    }

    /**
     * Resets the reCAPTCHA widget. This resets state and invalidates the token.
     */
    function resetReCAPTCHA() {
        setReCAPTCHAToken(null);

        if (widgetId) {
            window.grecaptcha.reset(widgetId);
        }
    }

    return {
        reCAPTCHAToken,
        renderReCAPTCHA,
        resetReCAPTCHA,
    };
}

/**
 * Renders a Google ReCAPTCHA.
 *
 * @note The Google reCAPTCHA script must be loaded for this component to work. This component will throw an error if the script is not loaded.
 *
 * @example
 * // Loading the Google reCAPTCHA script in index.html
 * <html>
 *  <head>
 *    <script src="https://www.google.com/recaptcha/api.js" async defer></script>
 *  </head>
 *  ...
 * </html>
 *
 * @param {object} props
 * @param {(ref: React.MutableRefObject<HTMLElement | null>) => void} props.render Function for rendering the reCAPTCHA widget. Use the `useReCAPTCHA` hook to get this function.
 */
function ReCAPTCHA({ render }) {
    /** @type {React.MutableRefObject<HTMLDivElement | null>} */
    const recaptchaRef = useRef(null);

    useEffect(() => {
        // Only render the reCAPTCHA widget once
        if (!recaptchaRef.current?.hasChildNodes()) {
            render(recaptchaRef);
        }
    }, []);

    return <div ref={recaptchaRef} />;
}

export default ReCAPTCHA;
